import { useCallback, useEffect, useState } from "react";
import { CardOnlyI } from "../../../../../api/request/Payment/Interfaces/PaymentMethodI.response";

/**
 * Suscripciones «solo tarjeta» (ms-payment): el portal no ofrece IBAN/SEPA a quien
 * solo puede pagar con tarjeta.
 *
 * - Capa principal: `GET /api/payment-method/card-only/{ulid}` al abrir el
 *   formulario de método de pago. Con `cardOnly: true` se oculta la opción SEPA.
 * - Red: `PUT /api/payment-method/update-payment-method` responde 409
 *   `{"error":"CARD_ONLY_SUBSCRIPTION"}` si aun así llega un SEPA.
 *
 * Si la lectura falla o todavía no existe (backend sin desplegar), NO se oculta
 * nada: el 409 sigue de red.
 */
export const CARD_ONLY_SUBSCRIPTION = "CARD_ONLY_SUBSCRIPTION";

/** Clave pendiente de crear en `getlife/translations` (ver docs/i18n-pending.md). */
export const CARD_ONLY_MESSAGE_KEY = "dashboard.policy.alert.paymentMethod.cardOnly";

/**
 * Texto aprobado (aviso «solo tarjeta» del orquestador de correo, parte neutra:
 * sin mencionar disputas, porque la marca también puede ser manual). Igual en ES
 * y AXA_WL_ES; es el valor por defecto de cualquier mercado sin traducción.
 */
export const CARD_ONLY_MESSAGE_FALLBACK =
  "Los recibos de tu póliza solo se pueden pagar con tarjeta. Para que tu póliza siga al día, añade una tarjeta.";

export type TranslateFn = (key: string) => string;

export const cardOnlyMessage = (translate: TranslateFn): string => {
  const translated = translate(CARD_ONLY_MESSAGE_KEY);
  if (!translated || CARD_ONLY_MESSAGE_KEY === translated) {
    return CARD_ONLY_MESSAGE_FALLBACK;
  }
  return translated;
};

/** ¿Es el 409 «solo tarjeta» de update-payment-method? (ver HTTPService.parseError). */
export const isCardOnlyRefusal = (error: unknown): boolean => {
  if (!error || "object" !== typeof error) {
    return false;
  }
  const { status, error: code, message } = error as {
    status?: unknown;
    error?: unknown;
    message?: unknown;
  };
  return (
    409 === Number(status) &&
    (CARD_ONLY_SUBSCRIPTION === code || CARD_ONLY_SUBSCRIPTION === message)
  );
};

export interface CardOnlyReader {
  getCardOnly: () => Promise<CardOnlyI>;
}

/** Solo un `cardOnly: true` explícito oculta el IBAN; cualquier fallo, no. */
export const readCardOnly = async (repository: CardOnlyReader): Promise<boolean> => {
  try {
    const response = await repository.getCardOnly();
    return true === response?.cardOnly;
  } catch (error) {
    return false;
  }
};

/**
 * Pregunta la marca cada vez que `active` pasa a `true` (se abre el formulario).
 * `markCardOnly` lo usa la red del 409 para pasar a «solo tarjeta» sin releer.
 */
export const useCardOnly = (repository: CardOnlyReader, active: boolean) => {
  const [cardOnly, setCardOnly] = useState<boolean>(false);

  useEffect(() => {
    if (!active) {
      return;
    }
    let cancelled = false;
    readCardOnly(repository).then((value) => {
      if (!cancelled) {
        setCardOnly(value);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const markCardOnly = useCallback(() => setCardOnly(true), []);

  return { cardOnly, markCardOnly };
};
